/**
 * DviStagePublish - Stage 6: Customer publish and response tracking
 * 
 * Share inspection with customer and track their responses.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
// DTC-K: route the DVI session-token expiry comparison through the
// canonical clock so the publish-flow business decision is
// deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Share2, 
  Loader2, 
  Copy,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  Clock,
  CheckCircle,
  Info,
  Archive
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { FindingGroup } from '@/hooks/useDviFindingGroups';

interface DviStagePublishProps {
  session: {
    id: string;
    status: string;
    // Phase 1A: customer_view_token removed — derive "shared" from status
    token_expires_at?: string | null;
  } | null;
  findingGroups: FindingGroup[];
  approvedByCustomer: FindingGroup[];
  declinedByCustomer: FindingGroup[];
  onShare: () => Promise<{ share_url: string } | undefined>;
  isSharing: boolean;
  canApprove: boolean;
  isLoading: boolean;
}

const SESSION_STATUS_CONFIG: Record<string, { label: string; className: string; icon: React.ComponentType<{ className?: string }> }> = {
  draft: { label: 'Draft', className: 'bg-muted text-muted-foreground', icon: Clock },
  shared: { label: 'Shared', className: 'bg-blue-500/10 text-blue-600', icon: Share2 },
  customer_viewed: { label: 'Viewed', className: 'bg-purple-500/10 text-purple-600', icon: ExternalLink },
  customer_responded: { label: 'Responded', className: 'bg-green-500/10 text-green-600', icon: CheckCircle },
  archived: { label: 'Archived', className: 'bg-muted text-muted-foreground', icon: Archive },
};

export function DviStagePublish({
  session,
  findingGroups,
  approvedByCustomer,
  declinedByCustomer,
  onShare,
  isSharing,
  canApprove,
  isLoading,
}: DviStagePublishProps) {
  const { toast } = useToast();
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="text-center py-8 space-y-2">
        <Share2 className="h-12 w-12 text-muted-foreground mx-auto" />
        <p className="text-sm text-muted-foreground">No session to publish</p>
        <p className="text-xs text-muted-foreground">
          Create a session first
        </p>
      </div>
    );
  }

  const statusConfig = SESSION_STATUS_CONFIG[session.status] || SESSION_STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  // Phase 1A: Derive "has been shared" from status instead of checking plaintext token
  const hasToken = session.status !== 'draft';
  const tokenExpired = session.token_expires_at && new Date(session.token_expires_at) < clock.now();
  const pendingCount = findingGroups.filter((g) => g.customer_decision === 'none').length;

  const handleShare = async () => {
    const result = await onShare();
    if (result?.share_url) {
      setShareUrl(result.share_url);
    }
  };

  const copyLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      toast({ title: 'Link copied to clipboard' });
    }
  };

  const openCustomerView = () => {
    if (shareUrl) {
      window.open(shareUrl, '_blank');
    }
  };

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <StatusIcon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Publish Status</span>
              <Badge variant="outline" className={cn('text-xs', statusConfig.className)}>
                {statusConfig.label}
              </Badge>
            </div>
          </div>

          {/* Token status */}
          {hasToken && !tokenExpired && (
            <div className="bg-green-50/50 border border-green-200 rounded p-2 flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
              <div className="text-xs text-green-700">
                <p className="font-medium">Customer link active</p>
                {session.token_expires_at && (
                  <p className="text-green-600">
                    Expires: {new Date(session.token_expires_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          )}

          {hasToken && tokenExpired && (
            <div className="bg-yellow-50/50 border border-yellow-200 rounded p-2 flex items-start gap-2">
              <Clock className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
              <div className="text-xs text-yellow-700">
                <p className="font-medium">Link expired</p>
                <p className="text-yellow-600">Generate a new link to share</p>
              </div>
            </div>
          )}

          {/* Share actions */}
          {canApprove && (
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={handleShare}
                disabled={isSharing}
                className="gap-1.5"
              >
                {isSharing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Share2 className="h-3 w-3" />
                )}
                {hasToken && !tokenExpired ? 'Regenerate Link' : 'Share with Customer'}
              </Button>

              {shareUrl && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copyLink}
                    className="gap-1.5"
                  >
                    <Copy className="h-3 w-3" />
                    Copy Link
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={openCustomerView}
                    className="gap-1.5"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Preview
                  </Button>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Response Summary */}
      {session.status === 'customer_responded' && (
        <Card>
          <CardContent className="p-3 space-y-2">
            <p className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Customer Response
            </p>
            
            <div className="flex items-center gap-4 flex-wrap">
              {approvedByCustomer.length > 0 && (
                <div className="flex items-center gap-1.5 text-green-600">
                  <ThumbsUp className="h-4 w-4" />
                  <span className="text-sm font-medium">{approvedByCustomer.length} approved</span>
                </div>
              )}
              {declinedByCustomer.length > 0 && (
                <div className="flex items-center gap-1.5 text-red-600">
                  <ThumbsDown className="h-4 w-4" />
                  <span className="text-sm font-medium">{declinedByCustomer.length} declined</span>
                </div>
              )}
              {pendingCount > 0 && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">{pendingCount} pending</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info about customer portal */}
      <div className="bg-muted/50 rounded p-2 flex items-start gap-2">
        <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">
          When shared, customers receive a link to view findings with photos and approve/decline recommended services.
          The link expires after 7 days.
        </p>
      </div>
    </div>
  );
}
